import React, { useRef, useState, useCallback, useEffect, useMemo, MouseEvent } from 'react';
import { getBase64 } from 'helpers/getBase64';
import { Button } from 'components/ui/Button';
import { ImageIcon } from 'components/icons/Icons';

interface ImageUploadProps {
  value?: File | null;
  onChange: (file: File | null) => void;
  onError?: (error: string) => void;
  onRemove?: () => void;
  label?: string;
  placeholder?: string;
  dragPlaceholder?: string;
  acceptedTypes?: string[];
  maxSizeInMB?: number;
  height?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  showPreview?: boolean;
  allowRemove?: boolean;
  allowChange?: boolean;
  previewUrl?: string | null;
}

const DEFAULT_ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const DEFAULT_MAX_SIZE_MB = 5;

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  onError,
  label = 'Photo',
  placeholder = 'Ajouter une photo',
  dragPlaceholder = 'Glissez-déposez ou cliquez pour sélectionner',
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  maxSizeInMB = DEFAULT_MAX_SIZE_MB,
  height = 'h-full',
  disabled = false,
  required = false,
  onRemove,
  className = '',
  showPreview = true,
  allowRemove = true,
  allowChange = true,
  previewUrl
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  const currentFileRef = useRef<File | null>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [base64Preview, setBase64Preview] = useState<string | null>(null);

  // Memoization des types acceptés pour éviter les re-renders
  const acceptedTypesString = useMemo(() => acceptedTypes.join(','), [acceptedTypes]);
  const acceptedTypesDisplay = useMemo(() =>
    acceptedTypes.map(type => type.split('/')[1].toUpperCase()).join(', '),
    [acceptedTypes]
  );

  // Fonction de nettoyage des URLs
  const cleanupPreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  // Validation du fichier
  const validateFile = useCallback((file: File): string | null => {
    if (!acceptedTypes.includes(file.type)) {
      return `Type de fichier non supporté. Formats acceptés: ${acceptedTypesDisplay}`;
    }

    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return `Le fichier est trop volumineux (max ${maxSizeInMB}MB)`;
    }

    return null;
  }, [acceptedTypes, acceptedTypesDisplay, maxSizeInMB]);

  // Gestion de l'aperçu
  const updatePreview = useCallback(async (file: File | null) => {
    // Nettoyer l'ancien aperçu
    cleanupPreviewUrl();
    setBase64Preview(null);

    if (!file || !showPreview) {
      return;
    }

    try {
      // Créer l'URL d'objet pour l'aperçu immédiat
      previewUrlRef.current = URL.createObjectURL(file);

      // Optionnellement, créer l'aperçu base64 en arrière-plan
      const base64 = await getBase64(file);
      setBase64Preview(base64);
    } catch (error) {
      console.warn('Erreur lors de la génération de l\'aperçu base64:', error);
      // L'object URL reste disponible même si le base64 échoue
    }
  }, [showPreview, cleanupPreviewUrl]);

  // Gestion de l'upload
  const handleFileUpload = useCallback(async (file: File) => {
    setError(null);
    setIsUploading(true);
    setIsDragOver(false);

    try {
      // Validation
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        onError?.(validationError);
        return;
      }

      // Mettre à jour l'aperçu
      await updatePreview(file);

      // Mettre à jour la référence du fichier actuel
      currentFileRef.current = file;

      // Callback avec le fichier
      onChange(file);

    } catch (error) {
      console.error('Erreur upload:', error);
      const errorMessage = 'Erreur lors du téléchargement';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsUploading(false);
    }
  }, [validateFile, updatePreview, onChange, onError]);

  // Effect pour gérer les changements de fichier externe
  useEffect(() => {
    // Si le fichier a changé depuis l'extérieur
    if (value !== currentFileRef.current) {
      currentFileRef.current = value ?? null;
      updatePreview(value ?? null);
    }
  }, [value, updatePreview]);

  // Nettoyage au démontage
  useEffect(() => {
    return () => {
      cleanupPreviewUrl();
    };
  }, [cleanupPreviewUrl]);

  // Handlers d'événements
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset de l'input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, [disabled, handleFileUpload]);

  const openFileDialog = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const handleRemove = useCallback(() => {
    onRemove && onRemove()
    cleanupPreviewUrl();
    setBase64Preview(null);
    setError(null);
    currentFileRef.current = null;
    onChange(null);
  }, [cleanupPreviewUrl, onChange, onRemove]);

  const handleChangeFile = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    openFileDialog();
  }, [openFileDialog]);

  const handleRemoveFile = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    handleRemove();
  }, [handleRemove]);

  // Détermine l'aperçu à afficher (préfère base64 si disponible, sinon object URL)
  const currentPreview = useMemo(() => {
    // Priorité 1 : URL externe fournie
    if (previewUrl) {
      return previewUrl;
    }
    // Priorité 2 : Base64 du fichier uploadé
    if (base64Preview) {
      return base64Preview;
    }
    // Priorité 3 : Object URL du fichier
    return previewUrlRef.current;
  }, [previewUrl, base64Preview]);

  const hasImage = Boolean(currentPreview);

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Zone d'upload */}
      <div
        className={`
          relative bg-white group transition-all duration-200
          ${disabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
          ${isDragOver ? 'scale-[1.02]' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!hasImage ? openFileDialog : undefined}
      >
        {!hasImage ? (
          // Zone d'upload vide
          <div className={`
            p-6 ${height} border-2 border-dashed rounded-xl 
            flex flex-col items-center justify-center
            transition-colors duration-200
            ${isUploading
              ? 'border-gray-300 bg-gray-50'
              : isDragOver
                ? 'border-primary-dark bg-primary/10'
                : 'border-primary hover:border-primary-dark hover:bg-primary/5'
            }
          `}>
            {isUploading ? (
              <div className="flex flex-col items-center space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-sm text-gray-600">Téléchargement...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <ImageIcon className="w-12 text-primary/80" />
                <div className="text-center">
                  <p className="text-primary font-medium">
                    {isDragOver ? 'Déposez le fichier ici' : placeholder}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {dragPlaceholder}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {acceptedTypesDisplay} - Max {maxSizeInMB}MB
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="relative rounded-xl border border-gray-200 rounded-lg shadow-sm">
            <img
              src={currentPreview || ''}
              alt="Aperçu"
              className={`w-full rounded-xl max-w-full max-h-full object-contain ${height}`}
            />

            {(allowChange || allowRemove) && (
              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl flex items-center justify-center space-x-3">
                {allowChange && (
                  <Button
                    color="secondary"
                    onClick={handleChangeFile}
                  >
                    Changer
                  </Button>
                )}
                {allowRemove && (
                  <Button
                    color="primary"
                    onClick={handleRemoveFile}
                  >
                    Supprimer
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Input file caché */}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={acceptedTypesString}
          onChange={handleInputChange}
          disabled={disabled}
        />
      </div>

      {/* Gestion des erreurs */}
      {error && (
        <div className="flex items-center space-x-2 text-red-600 text-sm">
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
